import { useCallback, useEffect, useState } from 'react'
import { uniq } from 'lodash'

import { DataKey, StatisticBase } from 'modules/Monitoring/types'
import { getColorByIndex } from 'modules/Monitoring/utils/charts/getColorByIndex'
import { getKeyBase } from 'modules/Monitoring/utils/charts/getKeyBase'

export const usePrepareChartColorMap = <T extends StatisticBase, K extends keyof T>(
  dataKey: DataKey<T, K>,
  mainData: T[],
  additionalData?: T[],
) => {
  const [colorMap, setColorMap] = useState<Record<string, string>>({})
  const [resetCount, setResetCount] = useState(0)

  const resetColorMap = useCallback(() => {
    setColorMap({})
    setResetCount(count => count + 1)
  }, [])

  useEffect(() => {
    const mainValues = mainData.map(item => getKeyBase(item, dataKey))
    const additionalValues = additionalData
      ? additionalData.map(item => getKeyBase(item, dataKey))
      : []
    const allCurrentKeys = uniq([...mainValues, ...additionalValues])

    setColorMap(prevColorMap => {
      const currentMapKeys = Object.keys(prevColorMap)
      const newKeys = allCurrentKeys.filter(key => !prevColorMap[key])

      if (!newKeys.length) {
        return prevColorMap
      }

      const newColorMap = newKeys.reduce<Record<string, string>>((acc, curr, index) => {
        acc[curr] = getColorByIndex(index, currentMapKeys.length)
        return acc
      }, {})

      return {
        ...prevColorMap,
        ...newColorMap,
      }
    })
  }, [mainData, additionalData, dataKey, resetCount])

  return {
    colorMap,
    resetColorMap,
  }
}
